// 채팅 이모티콘(스티커) 카탈로그 — StickerPanel(웹·팝업 공용), 메시지 렌더링, 채팅목록 미리보기에서 사용
// 메시지 저장 형식: message_type="sticker", content="팩:번호" (예: "ione-m:01", "ione-f:27")
// 이미지는 frontend/public/stickers/{팩}/{번호}.png 정적 자산 (데스크톱 메신저는 서버 URL을 앞에 붙여 같은 파일을 사용)
// 데스크톱 desktop/src/renderer/src/lib/stickers.ts 와 목록을 같게 유지할 것
import type { MessageType } from "@/types/chat";

/** 캐릭터(팩) — 인사 기록 성별로 정해져 /users/me 의 sticker_character 로 내려옴 (본인 선택 없음) */
export type StickerCharacter = "male" | "female";
/** 팩이 누구에게 보이는지: 성별 팩은 male/female, 동물 팩은 all(남녀 모두) */
export type StickerAudience = StickerCharacter | "all";

export interface ChatSticker {
  /** 메시지 content 에 저장되는 ID ("팩:번호") */
  id: string;
  /** 접근성 alt / 툴팁용 이름 */
  label: string;
  /** 팩 ID (ione-m / ione-f / animal / legend …) */
  pack: string;
  audience: StickerAudience;
}

export const STICKER_PREVIEW_TEXT = "(이모티콘)";

/**
 * 스티커 이미지 캐시 버전. nginx 가 /stickers/*.png 를 1년 immutable 캐시로 내려주므로
 * 타일 파일을 교체하면 이 값을 올려야 이미 열어본 브라우저·데스크톱이 새 파일을 받는다.
 * (2026-09-23 v2: 여성 팩 투명 배경 복원) — 데스크톱 lib/stickers.ts 의 값과 함께 올릴 것
 */
export const STICKER_ASSET_VERSION = "2";

// 남녀 공통 24종 (두 시트의 1~4행 순서가 같음)
const COMMON_LABELS = [
  "최고예요!", "짝짝짝", "감사합니다", "꾸벅", "화이팅!", "감동이에요",
  "흠…", "깜짝!", "ㅋㅋㅋㅋ", "커피 한잔~", "죄송합니다 ㅠㅠ", "확인중…",
  "잘하고 있어요!", "잘자요", "음…", "안녕하세요!", "축하합니다!", "힘내세요!",
  "좋은 아이디어!", "다녀올게요~", "헐~", "헷…", "열받네!!", "아…하…",
];
// 여성 시트에만 있는 5행 8종
const FEMALE_EXTRA_LABELS = ["뇌정지…", "어질어질", "좀 봐요…", "지켜보고 있다…", "넵!!", "에휴…", "돈냄새~", "아무일도 없었다…"];

// 동물 캐릭터 25종 (12_51_34.png, 5×5) — 성별 무관 공용
const ANIMAL_LABELS = [
  "최고예요!", "신난다!", "대박!", "ㅋㅋㅋ", "사랑해요!",
  "헉!", "ㅋㅋㅋ", "감동이에요…", "열받아!", "죄송합니다…",
  "잘자요~", "음…?", "몰래보기", "확인중…", "화이팅!",
  "빡쳐!", "도망가자!", "살려줘…", "간지나지?", "좋은 아이디어!",
  "커피 한잔~", "귀여워!", "와아~", "넵!", "에휴…",
];

// 신수(용·봉황·거북 등) 캐릭터 25종 (01_05_11.png, 5×5) — 성별 무관 공용, 팩 ID "legend"
const LEGEND_LABELS = [
  "최고예요!", "신난다!", "감사합니다", "ㅋㅋㅋ", "사랑해요!",
  "헉!", "멋져!", "으잉…", "열받아!", "죄송합니다…",
  "잘자요~", "음…?", "몰래보기", "확인중…", "화이팅!",
  "빡쳐!", "좋은 아이디어!", "살려줘…", "간지나지?", "좋은 아이디어!",
  "커피 한잔~", "귀여워!", "와아~", "넵!", "에휴…",
];

// 호러(귀신·뱀파이어·강시) 캐릭터 25종 (01_13_18.png, 5×5) — 성별 무관 공용
const HORROR_LABELS = [
  "최고예요!", "신난다!", "감사합니다", "ㅋㅋㅋ", "사랑해요!",
  "헉!", "멋져!", "오잉…", "열받아!", "죄송합니다…",
  "잘자요~", "음…?", "몰래보기", "확인중…", "화이팅!",
  "빡쳐!", "좋은 아이디어!", "살려줘…", "간지나지?", "좋은 아이디어!",
  "커피 한잔~", "귀여워!", "와아~", "넵!", "에휴…",
];

// 외계인 캐릭터 25종 (01_30_33.png, 5×5) — 성별 무관 공용
const ALIEN_LABELS = [
  "최고예요!", "감사합니다", "신난다!", "수고했어요", "ㅋㅋㅋ",
  "헉!", "음…?", "울어요…", "열받아!", "잘자요~",
  "방! 터졌어!", "좋은 아이디어!", "몰래보기…", "확인중…", "화이팅!",
  "커피 한잔~", "귀여워!", "살려줘…", "간지나지?", "사랑해요!",
  "야근중…", "귀여워!", "와아~", "넵!", "에휴…",
];

// audience: 성별 팩은 male/female(사람 탭), 공용 팩은 all(팩마다 탭 하나 — 탭 이름은 label)
const PACKS: { pack: string; label: string; audience: StickerAudience; labels: string[] }[] = [
  { pack: "ione-m", label: "남성 캐릭터", audience: "male", labels: COMMON_LABELS },
  { pack: "ione-f", label: "여성 캐릭터", audience: "female", labels: [...COMMON_LABELS, ...FEMALE_EXTRA_LABELS] },
  { pack: "animal", label: "동물", audience: "all", labels: ANIMAL_LABELS },
  { pack: "legend", label: "신수", audience: "all", labels: LEGEND_LABELS },  // 탭 이름은 대표 지시로 "신수"(팩 ID 는 legend 유지)
  { pack: "horror", label: "호러", audience: "all", labels: HORROR_LABELS },
  { pack: "alien", label: "외계", audience: "all", labels: ALIEN_LABELS },  // 탭 이름은 대표 지시로 "외계"
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * 서버 정적 자산 /stickers/catalog.json 으로 내보내는 카탈로그 (vite.config.ts 의 stickerCatalogPlugin 이 빌드 시 생성).
 * 데스크톱 메신저는 실행 중 이 파일을 내려받아 앱 재배포 없이 새 팩·스티커를 반영한다.
 * 팩 추가/이름 변경 → 웹 배포만 하면 됨. 형식을 바꾸면 version 을 올리고 데스크톱 lib/stickers.ts 의 검증도 맞출 것.
 */
export const STICKER_CATALOG = {
  version: 1,
  assetVersion: STICKER_ASSET_VERSION,
  packs: PACKS.map((p) => ({ pack: p.pack, label: p.label, audience: p.audience, labels: p.labels })),
};

/** 전체 카탈로그 (렌더링·조회용 — 상대가 보낸 다른 캐릭터도 그려야 하므로 모든 팩 포함) */
export const CHAT_STICKERS: ChatSticker[] = PACKS.flatMap((p) =>
  p.labels.map((label, i) => ({ id: `${p.pack}:${pad2(i + 1)}`, label, pack: p.pack, audience: p.audience }))
);

const STICKER_BY_ID = new Map(CHAT_STICKERS.map((s) => [s.id, s]));

/** 탭 키: "person"(성별 캐릭터 팩) 또는 공용 팩 ID(animal, legend …) */
export type StickerTabKey = string;
export interface StickerTab {
  key: StickerTabKey;
  label: string;
  stickers: ChatSticker[];
}

/** 패널 탭 — "사람"(성별 캐릭터 팩) 뒤에 공용 팩(동물·전설 …)이 팩마다 한 탭씩 */
export function stickerTabsFor(character: StickerCharacter): StickerTab[] {
  const person: StickerTab = { key: "person", label: "사람", stickers: CHAT_STICKERS.filter((s) => s.audience === character) };
  const shared: StickerTab[] = PACKS.filter((p) => p.audience === "all").map((p) => ({
    key: p.pack,
    label: p.label,
    stickers: CHAT_STICKERS.filter((s) => s.pack === p.pack),
  }));
  return [person, ...shared].filter((t) => t.stickers.length > 0);
}

export function findSticker(content: string | null | undefined): ChatSticker | undefined {
  return content ? STICKER_BY_ID.get(content) : undefined;
}

/** 스티커 ID → 이미지 경로. 카탈로그에 없는 ID(구버전·손상)면 null */
export function stickerSrc(content: string | null | undefined): string | null {
  if (!findSticker(content)) return null;
  const [pack, no] = (content as string).split(":");
  return `/stickers/${pack}/${no}.png?v=${STICKER_ASSET_VERSION}`;
}

/** 채팅목록·답장 인용·알림 등 텍스트 미리보기. 스티커는 ID 대신 "(이모티콘)" */
export function messagePreviewText(messageType: MessageType | string | undefined, content: string | null | undefined): string {
  if (messageType === "sticker") return STICKER_PREVIEW_TEXT;
  return content ?? "";
}
